import { defineDspModule, type DspModuleInstance, type DspProcessBlock } from '../../dsp-module-contract/src/index.js';

export const PARTITION_SIZES = Object.freeze([64, 128, 256, 512] as const);
export type PartitionSize = (typeof PARTITION_SIZES)[number];
export const IR_FAMILIES = Object.freeze(['body', 'cabinet', 'room', 'plate'] as const);
export type IrFamily = (typeof IR_FAMILIES)[number];
export type IrNormalization = 'none' | 'peak' | 'energy';
export type IrProvenance = Readonly<{
  kind: 'generated' | 'user';
  id: string;
  seed?: number;
  generatorVersion?: string;
  sourceHash: string;
}>;
export interface IrLoadRequest {
  readonly left: Float32Array;
  readonly right: Float32Array;
  readonly provenance: IrProvenance;
  readonly normalization: IrNormalization;
}
export interface EngineControls { readonly wet: number; readonly dry: number; readonly outputGain: number; readonly bypass: boolean }
export type EngineControlUpdate = Partial<EngineControls>;
export interface ConvolutionOptions { readonly sampleRate: number; readonly maxBlockSize: number; readonly partitionSize?: PartitionSize }
export interface ProceduralIrOptions {
  readonly family: IrFamily;
  readonly sampleRate: number;
  readonly durationMs: number;
  readonly decaySeconds: number;
  readonly damping: number;
  readonly stereoWidth: number;
  readonly seed: number;
  readonly normalization?: IrNormalization;
  readonly id?: string;
}
export interface EngineReadback {
  readonly index: number;
  readonly loaded: boolean;
  readonly irFrames: number;
  readonly partitionSize: PartitionSize;
  readonly latencyFrames: number;
  readonly partitionCount: number;
  readonly normalization: IrNormalization | null;
  readonly normalizedPeak: number;
  readonly normalizedRms: number;
  readonly provenance: IrProvenance | null;
  readonly controls: Readonly<EngineControls>;
  readonly activeTailFrames: number;
  readonly processedFrames: number;
}

const MAX_IR_FRAMES = 262_144;
const GENERATOR_VERSION = 'mod-032-ir-v1';
const HASH_PATTERN = /^[0-9a-f]{64}$/i;
const DEFAULT_CONTROLS: Readonly<EngineControls> = Object.freeze({ wet: 1, dry: 0, outputGain: 1, bypass: false });
const clamp = (value: number): number => Math.max(-1, Math.min(1, value));
const finiteRange = (name: string, value: number, minimum: number, maximum: number): number => {
  if (!Number.isFinite(value) || value < minimum || value > maximum) throw new RangeError(`${name} must be finite and in [${minimum}, ${maximum}]`);
  return value;
};
const integerRange = (name: string, value: number, minimum: number, maximum: number): number => {
  if (!Number.isInteger(value) || value < minimum || value > maximum) throw new RangeError(`${name} must be an integer in [${minimum}, ${maximum}]`);
  return value;
};
const assertIndex = (index: number): void => { integerRange('engine index', index, 0, 3); };
const isPartitionSize = (value: number): value is PartitionSize => (PARTITION_SIZES as readonly number[]).includes(value);

class FftPlan {
  readonly size: number;
  private readonly cos: Float64Array;
  private readonly sin: Float64Array;
  constructor(size: number) {
    this.size = size;
    this.cos = new Float64Array(size / 2);
    this.sin = new Float64Array(size / 2);
    for (let i = 0; i < size / 2; i++) { const angle = -2 * Math.PI * i / size; this.cos[i] = Math.cos(angle); this.sin[i] = Math.sin(angle); }
  }
  transform(real: Float64Array, imaginary: Float64Array, inverse = false): void {
    const n = this.size;
    for (let i = 1, j = 0; i < n; i++) {
      let bit = n >> 1;
      for (; j & bit; bit >>= 1) j ^= bit;
      j ^= bit;
      if (i < j) { let value = real[i]!; real[i] = real[j]!; real[j] = value; value = imaginary[i]!; imaginary[i] = imaginary[j]!; imaginary[j] = value; }
    }
    for (let length = 2; length <= n; length <<= 1) {
      const half = length >> 1, step = n / length;
      for (let base = 0; base < n; base += length) for (let j = 0; j < half; j++) {
        const twiddle = j * step, cosine = this.cos[twiddle]!, sine = inverse ? -this.sin[twiddle]! : this.sin[twiddle]!;
        const even = base + j, odd = even + half;
        const oddReal = real[odd]! * cosine - imaginary[odd]! * sine;
        const oddImaginary = real[odd]! * sine + imaginary[odd]! * cosine;
        const evenReal = real[even]!, evenImaginary = imaginary[even]!;
        real[even] = evenReal + oddReal; imaginary[even] = evenImaginary + oddImaginary;
        real[odd] = evenReal - oddReal; imaginary[odd] = evenImaginary - oddImaginary;
      }
    }
    if (inverse) for (let i = 0; i < n; i++) { real[i] = real[i]! / n; imaginary[i] = imaginary[i]! / n; }
  }
}

interface NormalizedIr { left: Float32Array; right: Float32Array; peak: number; rms: number }
const normalizeIr = (leftSource: Float32Array, rightSource: Float32Array, normalization: IrNormalization): NormalizedIr => {
  if (!(leftSource instanceof Float32Array) || !(rightSource instanceof Float32Array)) throw new TypeError('IR channels must be Float32Array instances');
  if (leftSource.length < 1 || leftSource.length > MAX_IR_FRAMES || rightSource.length !== leftSource.length) throw new RangeError(`IR channels must have matching lengths in [1, ${MAX_IR_FRAMES}]`);
  let sourcePeak = 0, sumSquares = 0;
  for (let i = 0; i < leftSource.length; i++) {
    const left = leftSource[i]!, right = rightSource[i]!;
    if (!Number.isFinite(left) || !Number.isFinite(right)) throw new RangeError('IR samples must be finite');
    sourcePeak = Math.max(sourcePeak, Math.abs(left), Math.abs(right)); sumSquares += left * left + right * right;
  }
  if (normalization !== 'none' && normalization !== 'peak' && normalization !== 'energy') throw new RangeError('normalization must be none, peak or energy');
  const sourceRms = Math.sqrt(sumSquares / (leftSource.length * 2));
  let scale = 1;
  if (normalization === 'peak' && sourcePeak > 0) scale = 1 / sourcePeak;
  if (normalization === 'energy' && sourceRms > 0) scale = Math.min(1 / sourceRms, sourcePeak > 0 ? 1 / sourcePeak : Number.POSITIVE_INFINITY);
  const left = new Float32Array(leftSource.length), right = new Float32Array(rightSource.length);
  let peak = 0, normalizedSquares = 0;
  for (let i = 0; i < left.length; i++) {
    left[i] = leftSource[i]! * scale; right[i] = rightSource[i]! * scale;
    peak = Math.max(peak, Math.abs(left[i]!), Math.abs(right[i]!)); normalizedSquares += left[i]! * left[i]! + right[i]! * right[i]!;
  }
  return { left, right, peak, rms: Math.sqrt(normalizedSquares / (left.length * 2)) };
};

const validateProvenance = (source: IrProvenance): IrProvenance => {
  if (source === null || typeof source !== 'object' || (source.kind !== 'generated' && source.kind !== 'user')) throw new RangeError('provenance kind must be generated or user');
  if (typeof source.id !== 'string' || source.id.trim().length === 0 || source.id.includes('\0')) throw new RangeError('provenance id must be non-empty');
  if (typeof source.sourceHash !== 'string' || !HASH_PATTERN.test(source.sourceHash)) throw new RangeError('sourceHash must be a 64-digit SHA-256-compatible hexadecimal value');
  if (source.seed !== undefined) integerRange('provenance seed', source.seed, 0, 0xffff_ffff);
  if (source.generatorVersion !== undefined && (typeof source.generatorVersion !== 'string' || source.generatorVersion.trim().length === 0)) throw new RangeError('generatorVersion must be non-empty');
  if (source.kind === 'generated' && (source.seed === undefined || source.generatorVersion === undefined)) throw new RangeError('generated provenance requires seed and generatorVersion');
  const value: { kind: 'generated' | 'user'; id: string; sourceHash: string; seed?: number; generatorVersion?: string } = { kind: source.kind, id: source.id, sourceHash: source.sourceHash.toLowerCase() };
  if (source.seed !== undefined) value.seed = source.seed;
  if (source.generatorVersion !== undefined) value.generatorVersion = source.generatorVersion;
  return Object.freeze(value);
};

class PartitionedChannel {
  private readonly partitionSize: number;
  private readonly fftSize: number;
  private readonly partitions: number;
  private readonly plan: FftPlan;
  private readonly irReal: Float64Array;
  private readonly irImaginary: Float64Array;
  private readonly historyReal: Float64Array;
  private readonly historyImaginary: Float64Array;
  private readonly workReal: Float64Array;
  private readonly workImaginary: Float64Array;
  readonly input: Float64Array;
  readonly output: Float64Array;
  private readonly overlap: Float64Array;
  private historyHead = 0;

  constructor(ir: Float32Array, partitionSize: number, plan: FftPlan) {
    this.partitionSize = partitionSize; this.fftSize = partitionSize * 2; this.partitions = Math.ceil(ir.length / partitionSize); this.plan = plan;
    const spectralLength = this.partitions * this.fftSize;
    this.irReal = new Float64Array(spectralLength); this.irImaginary = new Float64Array(spectralLength);
    this.historyReal = new Float64Array(spectralLength); this.historyImaginary = new Float64Array(spectralLength);
    this.workReal = new Float64Array(this.fftSize); this.workImaginary = new Float64Array(this.fftSize);
    this.input = new Float64Array(partitionSize); this.output = new Float64Array(partitionSize); this.overlap = new Float64Array(partitionSize);
    for (let partition = 0; partition < this.partitions; partition++) {
      this.workReal.fill(0); this.workImaginary.fill(0);
      const start = partition * partitionSize;
      for (let i = 0; i < partitionSize && start + i < ir.length; i++) this.workReal[i] = ir[start + i]!;
      this.plan.transform(this.workReal, this.workImaginary);
      this.irReal.set(this.workReal, partition * this.fftSize); this.irImaginary.set(this.workImaginary, partition * this.fftSize);
    }
    this.workReal.fill(0); this.workImaginary.fill(0);
  }

  processPartition(): void {
    this.workReal.fill(0); this.workImaginary.fill(0);
    this.workReal.set(this.input); this.plan.transform(this.workReal, this.workImaginary);
    const historyOffset = this.historyHead * this.fftSize;
    this.historyReal.set(this.workReal, historyOffset); this.historyImaginary.set(this.workImaginary, historyOffset);
    this.workReal.fill(0); this.workImaginary.fill(0);
    for (let irPartition = 0; irPartition < this.partitions; irPartition++) {
      const historyPartition = (this.historyHead - irPartition + this.partitions) % this.partitions;
      const inputOffset = historyPartition * this.fftSize, irOffset = irPartition * this.fftSize;
      for (let bin = 0; bin < this.fftSize; bin++) {
        const xr = this.historyReal[inputOffset + bin]!, xi = this.historyImaginary[inputOffset + bin]!, hr = this.irReal[irOffset + bin]!, hi = this.irImaginary[irOffset + bin]!;
        this.workReal[bin] = this.workReal[bin]! + xr * hr - xi * hi; this.workImaginary[bin] = this.workImaginary[bin]! + xr * hi + xi * hr;
      }
    }
    this.plan.transform(this.workReal, this.workImaginary, true);
    for (let i = 0; i < this.partitionSize; i++) { this.output[i] = this.workReal[i]! + this.overlap[i]!; this.overlap[i] = this.workReal[i + this.partitionSize]!; }
    this.historyHead = (this.historyHead + 1) % this.partitions;
  }

  reset(): void {
    this.historyReal.fill(0); this.historyImaginary.fill(0); this.workReal.fill(0); this.workImaginary.fill(0);
    this.input.fill(0); this.output.fill(0); this.overlap.fill(0); this.historyHead = 0;
  }
}

interface LoadedEngine {
  readonly left: PartitionedChannel;
  readonly right: PartitionedChannel;
  readonly irFrames: number;
  readonly partitionCount: number;
  readonly normalization: IrNormalization;
  readonly normalizedPeak: number;
  readonly normalizedRms: number;
  readonly provenance: IrProvenance;
}
interface EngineState {
  loaded: LoadedEngine | null;
  wet: number;
  dry: number;
  outputGain: number;
  bypass: boolean;
  dryLeft: Float32Array;
  dryRight: Float32Array;
  position: number;
  activeTailFrames: number;
  processedFrames: number;
}

export class ConvolutionBodyIrProcessor {
  readonly sampleRate: number;
  readonly maxBlockSize: number;
  readonly partitionSize: PartitionSize;
  readonly latencyFrames: number;
  readonly engineCount = 4;
  private readonly plan: FftPlan;
  private readonly engines: EngineState[];

  constructor(options: ConvolutionOptions) {
    this.sampleRate = integerRange('sampleRate', options.sampleRate, 8000, 192_000);
    this.maxBlockSize = integerRange('maxBlockSize', options.maxBlockSize, 1, 8192);
    const partitionSize = options.partitionSize ?? 128;
    if (!isPartitionSize(partitionSize)) throw new RangeError('partitionSize must be 64, 128, 256 or 512');
    this.partitionSize = partitionSize; this.latencyFrames = partitionSize; this.plan = new FftPlan(partitionSize * 2);
    this.engines = Array.from({ length: 4 }, () => ({ loaded: null, wet: DEFAULT_CONTROLS.wet, dry: DEFAULT_CONTROLS.dry, outputGain: DEFAULT_CONTROLS.outputGain, bypass: DEFAULT_CONTROLS.bypass, dryLeft: new Float32Array(partitionSize), dryRight: new Float32Array(partitionSize), position: 0, activeTailFrames: 0, processedFrames: 0 }));
  }

  loadIr(index: number, request: IrLoadRequest): void {
    assertIndex(index);
    if (request === null || typeof request !== 'object') throw new TypeError('IR request is required');
    const provenance = validateProvenance(request.provenance);
    const normalized = normalizeIr(request.left, request.right, request.normalization);
    const loaded: LoadedEngine = Object.freeze({
      left: new PartitionedChannel(normalized.left, this.partitionSize, this.plan), right: new PartitionedChannel(normalized.right, this.partitionSize, this.plan),
      irFrames: normalized.left.length, partitionCount: Math.ceil(normalized.left.length / this.partitionSize), normalization: request.normalization,
      normalizedPeak: normalized.peak, normalizedRms: normalized.rms, provenance,
    });
    this.engines[index]!.loaded = loaded; this.resetEngine(this.engines[index]!);
  }
  load(index: number, request: IrLoadRequest): void { this.loadIr(index, request); }

  clearIr(index: number): void { assertIndex(index); this.engines[index]!.loaded = null; this.resetEngine(this.engines[index]!); }
  clear(index: number): void { this.clearIr(index); }

  setControls(index: number, update: EngineControlUpdate): void {
    assertIndex(index);
    if (update === null || typeof update !== 'object') throw new TypeError('controls update is required');
    for (const id in update) if (id !== 'wet' && id !== 'dry' && id !== 'outputGain' && id !== 'bypass') throw new RangeError(`unknown control: ${id}`);
    const engine = this.engines[index]!;
    const wet = finiteRange('wet', update.wet ?? engine.wet, 0, 1), dry = finiteRange('dry', update.dry ?? engine.dry, 0, 1), outputGain = finiteRange('outputGain', update.outputGain ?? engine.outputGain, 0, 1);
    const bypass = update.bypass ?? engine.bypass;
    if (typeof bypass !== 'boolean') throw new TypeError('bypass must be boolean');
    engine.wet = wet; engine.dry = dry; engine.outputGain = outputGain; engine.bypass = bypass;
  }
  configure(index: number, update: EngineControlUpdate): void { this.setControls(index, update); }

  processInto(index: number, inputLeft: Float32Array, inputRight: Float32Array, outputLeft: Float32Array, outputRight: Float32Array, frames = inputLeft.length): void {
    assertIndex(index);
    integerRange('frames', frames, 1, this.maxBlockSize);
    if (!(inputLeft instanceof Float32Array) || !(inputRight instanceof Float32Array) || !(outputLeft instanceof Float32Array) || !(outputRight instanceof Float32Array)) throw new TypeError('audio channels must be Float32Array instances');
    if (inputLeft.length < frames || inputRight.length < frames || outputLeft.length < frames || outputRight.length < frames) throw new RangeError('all channel buffers must cover frames');
    for (let i = 0; i < frames; i++) if (!Number.isFinite(inputLeft[i]!) || !Number.isFinite(inputRight[i]!)) throw new RangeError('input samples must be finite');

    const engine = this.engines[index]!, loaded = engine.loaded;
    const wetGain = Math.sqrt(engine.wet), dryGain = Math.sqrt(engine.dry), gain = engine.outputGain, bypass = engine.bypass;
    for (let i = 0; i < frames; i++) {
      const position = engine.position, inputL = inputLeft[i]!, inputR = inputRight[i]!;
      const delayedL = engine.dryLeft[position]!, delayedR = engine.dryRight[position]!;
      engine.dryLeft[position] = inputL; engine.dryRight[position] = inputR;
      const wetL = loaded === null ? 0 : loaded.left.output[position]!, wetR = loaded === null ? 0 : loaded.right.output[position]!;
      if (bypass) { outputLeft[i] = clamp(delayedL); outputRight[i] = clamp(delayedR); }
      else { outputLeft[i] = clamp((delayedL * dryGain + wetL * wetGain) * gain); outputRight[i] = clamp((delayedR * dryGain + wetR * wetGain) * gain); }
      if (loaded !== null) { loaded.left.input[position] = inputL; loaded.right.input[position] = inputR; }
      const excited = loaded !== null && loaded.normalizedPeak > 0 && (inputL !== 0 || inputR !== 0);
      if (excited) engine.activeTailFrames = Math.max(engine.activeTailFrames, this.partitionSize + loaded.irFrames);
      if (engine.activeTailFrames > 0) engine.activeTailFrames--;
      engine.position++;
      if (engine.position === this.partitionSize) { if (loaded !== null) { loaded.left.processPartition(); loaded.right.processPartition(); } engine.position = 0; }
      engine.processedFrames++;
    }
  }
  process(index: number, inputLeft: Float32Array, inputRight: Float32Array, outputLeft: Float32Array, outputRight: Float32Array, frames = inputLeft.length): void { this.processInto(index, inputLeft, inputRight, outputLeft, outputRight, frames); }

  reset(index?: number): void {
    if (index === undefined) { for (const engine of this.engines) this.resetEngine(engine); return; }
    assertIndex(index); this.resetEngine(this.engines[index]!);
  }
  private resetEngine(engine: EngineState): void {
    engine.dryLeft.fill(0); engine.dryRight.fill(0); engine.position = 0; engine.activeTailFrames = 0; engine.processedFrames = 0;
    engine.loaded?.left.reset(); engine.loaded?.right.reset();
  }

  readback(index: number): EngineReadback {
    assertIndex(index); const engine = this.engines[index]!, loaded = engine.loaded;
    return Object.freeze({ index, loaded: loaded !== null, irFrames: loaded?.irFrames ?? 0, partitionSize: this.partitionSize, latencyFrames: this.partitionSize,
      partitionCount: loaded?.partitionCount ?? 0, normalization: loaded?.normalization ?? null, normalizedPeak: loaded?.normalizedPeak ?? 0, normalizedRms: loaded?.normalizedRms ?? 0,
      provenance: loaded?.provenance ?? null, controls: Object.freeze({ wet: engine.wet, dry: engine.dry, outputGain: engine.outputGain, bypass: engine.bypass }), activeTailFrames: engine.activeTailFrames, processedFrames: engine.processedFrames });
  }
  getReadback(index: number): EngineReadback { return this.readback(index); }
  controlValue(index: number, id: 'wet' | 'dry' | 'outputGain' | 'bypass'): number | boolean {
    assertIndex(index); const engine = this.engines[index]!; return engine[id];
  }
}

export { ConvolutionBodyIrProcessor as PartitionedConvolutionProcessor, ConvolutionBodyIrProcessor as FourEngineConvolutionProcessor };

const rotateRight = (value: number, shift: number): number => (value >>> shift) | (value << (32 - shift));
const sha256 = (text: string): string => {
  const constants = new Uint32Array([0x428a2f98,0x71374491,0xb5c0fbcf,0xe9b5dba5,0x3956c25b,0x59f111f1,0x923f82a4,0xab1c5ed5,0xd807aa98,0x12835b01,0x243185be,0x550c7dc3,0x72be5d74,0x80deb1fe,0x9bdc06a7,0xc19bf174,0xe49b69c1,0xefbe4786,0x0fc19dc6,0x240ca1cc,0x2de92c6f,0x4a7484aa,0x5cb0a9dc,0x76f988da,0x983e5152,0xa831c66d,0xb00327c8,0xbf597fc7,0xc6e00bf3,0xd5a79147,0x06ca6351,0x14292967,0x27b70a85,0x2e1b2138,0x4d2c6dfc,0x53380d13,0x650a7354,0x766a0abb,0x81c2c92e,0x92722c85,0xa2bfe8a1,0xa81a664b,0xc24b8b70,0xc76c51a3,0xd192e819,0xd6990624,0xf40e3585,0x106aa070,0x19a4c116,0x1e376c08,0x2748774c,0x34b0bcb5,0x391c0cb3,0x4ed8aa4a,0x5b9cca4f,0x682e6ff3,0x748f82ee,0x78a5636f,0x84c87814,0x8cc70208,0x90befffa,0xa4506ceb,0xbef9a3f7,0xc67178f2]);
  const source = new TextEncoder().encode(text), bitLength = source.length * 8, byteLength = Math.ceil((source.length + 9) / 64) * 64, bytes = new Uint8Array(byteLength); bytes.set(source); bytes[source.length] = 0x80;
  const view = new DataView(bytes.buffer); view.setUint32(byteLength - 4, bitLength >>> 0); view.setUint32(byteLength - 8, Math.floor(bitLength / 0x1_0000_0000));
  const state = new Uint32Array([0x6a09e667,0xbb67ae85,0x3c6ef372,0xa54ff53a,0x510e527f,0x9b05688c,0x1f83d9ab,0x5be0cd19]), words = new Uint32Array(64);
  for (let offset = 0; offset < byteLength; offset += 64) {
    for (let i = 0; i < 16; i++) words[i] = view.getUint32(offset + i * 4);
    for (let i = 16; i < 64; i++) { const a = words[i - 15]!, b = words[i - 2]!, s0 = rotateRight(a,7)^rotateRight(a,18)^(a>>>3), s1 = rotateRight(b,17)^rotateRight(b,19)^(b>>>10); words[i] = (words[i-16]! + s0 + words[i-7]! + s1) >>> 0; }
    let a=state[0]!,b=state[1]!,c=state[2]!,d=state[3]!,e=state[4]!,f=state[5]!,g=state[6]!,h=state[7]!;
    for(let i=0;i<64;i++){const s1=rotateRight(e,6)^rotateRight(e,11)^rotateRight(e,25),choice=(e&f)^(~e&g),t1=(h+s1+choice+constants[i]!+words[i]!)>>>0,s0=rotateRight(a,2)^rotateRight(a,13)^rotateRight(a,22),majority=(a&b)^(a&c)^(b&c),t2=(s0+majority)>>>0;h=g;g=f;f=e;e=(d+t1)>>>0;d=c;c=b;b=a;a=(t1+t2)>>>0;}
    state[0]=(state[0]!+a)>>>0;state[1]=(state[1]!+b)>>>0;state[2]=(state[2]!+c)>>>0;state[3]=(state[3]!+d)>>>0;state[4]=(state[4]!+e)>>>0;state[5]=(state[5]!+f)>>>0;state[6]=(state[6]!+g)>>>0;state[7]=(state[7]!+h)>>>0;
  }
  return [...state].map(value => value.toString(16).padStart(8, '0')).join('');
};
class XorShift32 { private state: number; constructor(seed: number) { this.state = seed || 0x6d2b79f5; } next(): number { let x=this.state;x^=x<<13;x^=x>>>17;x^=x<<5;this.state=x>>>0;return this.state/0x1_0000_0000; } bipolar(): number { return this.next()*2-1; } }

export const generateProceduralIr = (options: ProceduralIrOptions): IrLoadRequest => {
  if (!(IR_FAMILIES as readonly string[]).includes(options.family)) throw new RangeError('family must be body, cabinet, room or plate');
  const sampleRate=integerRange('sampleRate',options.sampleRate,8000,192_000),durationMs=finiteRange('durationMs',options.durationMs,5,5000),decay=finiteRange('decaySeconds',options.decaySeconds,.05,20),damping=finiteRange('damping',options.damping,0,1),width=finiteRange('stereoWidth',options.stereoWidth,0,1),seed=integerRange('seed',options.seed,0,0xffff_ffff);
  const frames=Math.max(1,Math.round(sampleRate*durationMs/1000)); if(frames>MAX_IR_FRAMES)throw new RangeError(`generated IR exceeds ${MAX_IR_FRAMES} frames`);
  const normalization=options.normalization??'peak'; if(normalization!=='none'&&normalization!=='peak'&&normalization!=='energy')throw new RangeError('normalization must be none, peak or energy');
  if(options.id!==undefined&&(typeof options.id!=='string'||options.id.trim().length===0||options.id.includes('\0')))throw new RangeError('id must be non-empty');
  const random=new XorShift32(seed),left=new Float32Array(frames),right=new Float32Array(frames),ln1000=Math.log(1000),family=options.family;
  let lowL=0,lowR=0;
  for(let i=0;i<frames;i++){
    const time=i/sampleRate,envelope=Math.exp(-ln1000*time/decay),common=random.bipolar(),side=random.bipolar()*width;
    let rawL=0,rawR=0;
    if(family==='body'){
      const modal=Math.sin(2*Math.PI*97*time)+.65*Math.sin(2*Math.PI*211*time)+.4*Math.sin(2*Math.PI*389*time);
      const modalR=Math.sin(2*Math.PI*103*time+.2*width)+.65*Math.sin(2*Math.PI*223*time)+.4*Math.sin(2*Math.PI*401*time);
      rawL=(modal*.7+common*.12)*envelope;rawR=(modalR*.7+(common*(1-width)+side)*.12)*envelope;
    }else if(family==='cabinet'){
      const inputL=common+side*.4,inputR=common-side*.4,coefficient=.06+(1-damping)*.5;lowL+=coefficient*(inputL-lowL);lowR+=coefficient*(inputR-lowR);
      const resonance=Math.sin(2*Math.PI*145*time)*.35+Math.sin(2*Math.PI*720*time)*.12;rawL=(lowL*.75+resonance)*envelope;rawR=(lowR*.75+resonance*(1-width*.15))*envelope;
    }else if(family==='room'){
      const sparse=i<sampleRate*.08&&random.next()<.055?random.bipolar()*1.7:0,late=(common+side)*.24,lateR=(common-side)*.24;
      rawL=(sparse+late)*envelope;rawR=(sparse*(1-width*.3)+lateR)*envelope;
    }else{
      const modes=Math.sin(2*Math.PI*317*time)+.55*Math.sin(2*Math.PI*613*time)+.33*Math.sin(2*Math.PI*1231*time);
      const modesR=Math.sin(2*Math.PI*331*time+.3*width)+.55*Math.sin(2*Math.PI*641*time)+.33*Math.sin(2*Math.PI*1217*time);
      rawL=(common*.34+modes*.35+side*.2)*envelope;rawR=(common*.34+modesR*.35-side*.2)*envelope;
    }
    const smooth=.015+damping*.48;lowL+=smooth*(rawL-lowL);lowR+=smooth*(rawR-lowR);left[i]=lowL;right[i]=lowR;
  }
  if(family==='room'){left[0]=1;right[0]=1-width*.1;}else if(family==='cabinet'){left[0]=left[0]!+.8;right[0]=right[0]!+.75;}else if(family==='body'){left[0]=left[0]!+.5;right[0]=right[0]!+.45;}else{left[0]=left[0]!+.35;right[0]=right[0]!+.3;}
  for(let i=0;i<frames;i++){const mid=(left[i]!+right[i]!)*.5,side=(left[i]!-right[i]!)*.5*width;left[i]=mid+side;right[i]=mid-side;}
  const normalized=normalizeIr(left,right,normalization),canonical=`${GENERATOR_VERSION}|${family}|${sampleRate}|${durationMs}|${decay}|${damping}|${width}|${seed}|${normalization}`;
  const sourceHash=sha256(canonical),provenance=Object.freeze({kind:'generated' as const,id:options.id??`${family}-${seed.toString(16).padStart(8,'0')}-${sourceHash.slice(0,12)}`,seed,generatorVersion:GENERATOR_VERSION,sourceHash});
  return Object.freeze({left:normalized.left,right:normalized.right,normalization,provenance});
};
export const createProceduralIr = generateProceduralIr;

class ModuleInstance implements DspModuleInstance {
  readonly processor: ConvolutionBodyIrProcessor;
  private engineIndex=0;
  constructor(sampleRate:number,private readonly maxBlockSize:number){this.processor=new ConvolutionBodyIrProcessor({sampleRate,maxBlockSize});}
  reset():void{this.processor.reset();}
  setParameter(id:string,value:number):void{
    if(id==='engine-index'){this.engineIndex=integerRange(id,value,0,3);return;}
    if(id==='wet'||id==='dry'||id==='output-gain'||id==='bypass'){
      const update:EngineControlUpdate=id==='wet'?{wet:value}:id==='dry'?{dry:value}:id==='output-gain'?{outputGain:value}:{bypass:finiteRange(id,value,0,1)>=.5};this.processor.setControls(this.engineIndex,update);return;
    }
    throw new RangeError(`unknown parameter: ${id}`);
  }
  process(block:DspProcessBlock):void{
    integerRange('frames',block.frames,1,this.maxBlockSize);
    const engineValues=block.controls['engine-index'];let engine=engineValues&&engineValues.length?integerRange('engine-index',engineValues[0]!,0,3):this.engineIndex;
    let wet=this.processor.controlValue(engine,'wet') as number,dry=this.processor.controlValue(engine,'dry') as number,outputGain=this.processor.controlValue(engine,'outputGain') as number,bypass=this.processor.controlValue(engine,'bypass') as boolean;
    for(const id in block.controls){
      const values=block.controls[id]!;
      if(id!=='engine-index'&&id!=='wet'&&id!=='dry'&&id!=='output-gain'&&id!=='bypass')throw new RangeError(`unknown parameter: ${id}`);
      if(values.length===0||id==='engine-index')continue;
      const value=values[0]!;
      if(id==='wet')wet=finiteRange(id,value,0,1);else if(id==='dry')dry=finiteRange(id,value,0,1);else if(id==='output-gain')outputGain=finiteRange(id,value,0,1);else if(id==='bypass')bypass=finiteRange(id,value,0,1)>=.5;
    }
    const inputLeft=block.inputs['in-left'],inputRight=block.inputs['in-right'],outputLeft=block.outputs['out-left'],outputRight=block.outputs['out-right'];
    if(!inputLeft||!inputRight||!outputLeft||!outputRight)throw new RangeError('stereo input and output buffers are required');
    if(!(inputLeft instanceof Float32Array)||!(inputRight instanceof Float32Array)||!(outputLeft instanceof Float32Array)||!(outputRight instanceof Float32Array))throw new TypeError('audio channels must be Float32Array instances');
    if(inputLeft.length<block.frames||inputRight.length<block.frames||outputLeft.length<block.frames||outputRight.length<block.frames)throw new RangeError('all channel buffers must cover frames');
    for(let i=0;i<block.frames;i++)if(!Number.isFinite(inputLeft[i]!)||!Number.isFinite(inputRight[i]!))throw new RangeError('input samples must be finite');
    this.processor.setControls(engine,{wet,dry,outputGain,bypass});this.engineIndex=engine;
    this.processor.processInto(engine,inputLeft,inputRight,outputLeft,outputRight,block.frames);
  }
}
export const mod032=defineDspModule({descriptor:{id:'mir.mod-032',name:'Convolution body / IR processor',version:'0.1.0',inputs:[{id:'in-left',kind:'audio',channels:1},{id:'in-right',kind:'audio',channels:1}],outputs:[{id:'out-left',kind:'audio',channels:1},{id:'out-right',kind:'audio',channels:1}],parameters:[{id:'engine-index',defaultValue:0,minValue:0,maxValue:3,unit:'index'},{id:'wet',defaultValue:1,minValue:0,maxValue:1,unit:'ratio'},{id:'dry',defaultValue:0,minValue:0,maxValue:1,unit:'ratio'},{id:'output-gain',defaultValue:1,minValue:0,maxValue:1,unit:'ratio'},{id:'bypass',defaultValue:0,minValue:0,maxValue:1,unit:'boolean'}]},createInstance:context=>new ModuleInstance(context.sampleRate,context.maxBlockSize)});
export default mod032;
